import { motion, useReducedMotion } from 'framer-motion';
import { RedesignLayout } from 'root/src/client/v2/layout/features/RedesignLayout/RedesignLayout';
import { REDESIGN_NAV_OFFSET_CLASS, REDESIGN_NAV_STICKY_TOP_CLASS } from 'root/src/client/v2/layout/helpers/redesignNavbar';
import { TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { Toaster } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Shared chrome for the redesign static / content pages (legal pages, FAQ). A content page (not a
// hero): it offsets below the fixed navbar with REDESIGN_NAV_OFFSET_CLASS, constrains the reading
// width, gives a font-display header (icon + title + optional subtitle), and exposes a sticky sidebar
// slot for a table of contents (hidden on mobile, sticky under the navbar on `lg`). The page enters
// with one gentle fade / slide, respecting reduced motion. Zero layout shift: only opacity and a small
// Y translate animate, and the layout reserves its own space.
export const ContentPageLayout = ({ icon: Icon, title, subtitle, tableOfContents, children }) => {
    const reducedMotion = useReducedMotion();
    const hasToc = Boolean(tableOfContents);
    return (<RedesignLayout>
			<TooltipProvider delayDuration={200}>
				<div className={cn('min-h-screen', REDESIGN_NAV_OFFSET_CLASS)}>
					<motion.div initial={reducedMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
						<header className="mb-10 flex items-start gap-3">
							<div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15">
								<Icon sx={{ fontSize: 22 }} className="text-primary"/>
							</div>
							<div>
								<h1 className="font-display text-3xl font-bold text-foreground sm:text-4xl">{title}</h1>
								{subtitle ? (<p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>) : null}
							</div>
						</header>

						<div className={cn('flex flex-col gap-10', hasToc && 'lg:flex-row lg:items-start lg:gap-12')}>
							{hasToc ? (<aside className="lg:w-64 lg:flex-shrink-0">
									<div className={cn('lg:sticky', REDESIGN_NAV_STICKY_TOP_CLASS, 'lg:max-h-[calc(100vh-72px)] lg:overflow-y-auto lg:pt-6')}>
										<p className="mb-3 font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
											On this page
										</p>
										{tableOfContents}
									</div>
								</aside>) : null}

							<div className={cn('min-w-0 flex-1', hasToc ? 'lg:max-w-3xl' : 'max-w-3xl')}>
								{children}
							</div>
						</div>
					</motion.div>
				</div>
			</TooltipProvider>
			<Toaster />
		</RedesignLayout>);
};
